import { AfterViewInit, Directive, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatestWith, map, Observable, shareReplay, tap } from 'rxjs';
import { MenuScheduleDto } from 'src/app/api/v1/models';
import { ScrollListService } from 'src/app/services/scroll-list.sevice';
import { MenuListRepository } from 'src/app/shared/repositories/menu-list.repository';
import { SessionRepository } from 'src/app/shared/repositories/session.repository';
import { UIMenuItemDto } from '../../shared/interfaces/ui-menu-item-dto.interface';

@UntilDestroy()
@Directive()
export abstract class CategoryPageAbstract implements AfterViewInit {
  protected menuListRepository = inject(MenuListRepository);
  private scrollListService = inject(ScrollListService);
  private router = inject(Router);
  private sessionRepository = inject(SessionRepository)

  intersectionOptions = { rootMargin: '80px 0px 0px 0px', threshold: 0.3 };

  @Input() slug: string;

  schedule$: Observable<MenuScheduleDto | null> = this.menuListRepository.activeCategorySchedule$;

  items$: Observable<Map<string, UIMenuItemDto[]> | undefined> = this.menuListRepository.showItems$.pipe(
    combineLatestWith(
      this.sessionRepository.activeLabelFilter$,
      this.sessionRepository.activeAllergenFilter$
    ),
    map(([items, activeLabelFilter, activeAllergenFilter]) =>
      items
        ?.filter(i => !activeLabelFilter?.length || i.labels.some(l => activeLabelFilter.includes(l.name)))
        .filter(i => !activeAllergenFilter?.length || i.allergens.every(a => !activeAllergenFilter.includes(a.name)))
    ),
    map(items => items?.reduce(
      (arr, item) => arr.set(item.categoryId, (arr.get(item.categoryId) ?? []).concat([item])),
      new Map<string, UIMenuItemDto[]>()
    )),
    shareReplay(1),
  );

  categories$ = this.menuListRepository.menuNavCategories$.pipe(
    combineLatestWith(this.items$),
    map(([categories, items]) => categories?.filter(c => !c.isScheduleUnavailable && items?.has(c.id)))
  );

  activeCategoryIndex$: Observable<number> = this.scrollListService.activeIndexItem$;
  isSearching$: Observable<boolean> = this.menuListRepository.searchString$.pipe(map(str => !!str));
  activeFilterCount$: Observable<number> = this.sessionRepository.activeFilterCount$;

  ngAfterViewInit() {
    this.categories$.pipe(
      tap(categories => {
        const activeCategorySlug = categories?.find(c => c.id === this.menuListRepository.activeCategoryId)?.slug;
        const currentCategoryId = categories?.find(c => c.slug === this.slug)?.id;
        if (activeCategorySlug !== this.slug && currentCategoryId) {
          this.menuListRepository.setActiveCategory(currentCategoryId);
        }
      }),
      map(categories => categories?.map((x, i) => x.slug === this.slug ? i : 0).filter(x => !!x)[0] ?? 0),
      tap(i => setTimeout(() => this.scrollListService.setActiveIndex(i), 300)),
      untilDestroyed(this)
    ).subscribe();
  }

  leaveEnd(slug: string, isSearching: boolean | null) {
    if (isSearching || /\/category\/([\w|-]+)\/([\w|-]+)/.test(this.router.url)) { return; }
    this.menuListRepository.setActiveCategoryBySlug(slug);
  }
}
